import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { API_URL } from "../../config/apiConfig";

const Corporates = () => {
  const { user } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [savingIds, setSavingIds] = useState(() => new Set());
  const [staffModalAccount, setStaffModalAccount] = useState(null);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  
  // Rides Modal States
  const [ridesModalAccount, setRidesModalAccount] = useState(null);
  const [ridesLoading, setRidesLoading] = useState(false);
  const [ridesList, setRidesList] = useState([]);
  const [rideSearch, setRideSearch] = useState("");

  const token = useMemo(() => {
    if (user?.token) return user.token;
    if (user?.user?.token) return user.user.token;
    if (user?.data?.token) return user.data.token;
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      return stored?.token || stored?.user?.token || stored?.data?.token || null;
    } catch (e) {
      return null;
    }
  }, [user]);

  const config = useMemo(() => {
    if (token) {
      return { headers: { Authorization: `Bearer ${token}` } };
    }
    return {};
  }, [token]);

  const fetchAccounts = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/corporates`, config);
      if (res.data && (res.data.success || res.data.status)) {
        setAccounts(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setAccounts([]);
      }
    } catch (e) {
      setAccounts([]);
      toast.error(e?.response?.data?.message || e.message || "Failed to fetch corporate accounts");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStaff = async (account) => {
    if (!account?._id) return;
    setStaffModalAccount(account);
    setStaffList([]);
    setStaffLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/corporates/${account._id}/staff`, config);
      if (res.data && (res.data.success || res.data.status)) {
        setStaffList(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setStaffList([]);
      }
    } catch (e) {
      setStaffList([]);
      toast.error(e?.response?.data?.message || e.message || "Failed to fetch staff");
    } finally {
      setStaffLoading(false);
    }
  };

  // Fetch all corporate bookings and filter for this corporate account
  const fetchCorporateRides = async (account) => {
    if (!account?._id) return;
    setRidesModalAccount(account);
    setRidesList([]);
    setRidesLoading(true);
    setRideSearch("");
    try {
      const res = await axios.get(`${API_URL}/booking/admin/all`, config);
      if (res.data && res.data.success) {
        const allBookings = res.data.data || [];
        const filtered = allBookings.filter(
          (b) => b.corporateAccountId?._id === account._id || b.corporateAccountId === account._id
        );
        setRidesList(filtered);
      } else {
        setRidesList([]);
      }
    } catch (e) {
      setRidesList([]);
      toast.error(e?.response?.data?.message || e.message || "Failed to fetch corporate rides");
    } finally {
      setRidesLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const updateLocalField = (id, key, value) => {
    setAccounts((prev) =>
      prev.map((a) => {
        if (a._id !== id) return a;
        return { ...a, [key]: value };
      }),
    );
  };

  const saveAccount = async (account) => {
    setSavingIds((prev) => new Set([...prev, account._id]));
    try {
      const payload = {
        isActive: !!account.isActive,
        companyName: account.companyName,
        totalEmployees: Number(account.totalEmployees || 0),
        billingModel: account.billingModel,
        creditLimit: Number(account.creditLimit || 0),
      };

      if (account.billingModel === 'wallet') {
        payload.walletBalance = Number(account.walletBalance || 0);
      }

      const res = await axios.put(`${API_URL}/admin/corporates/${account._id}`, payload, config);
      if (res.data && (res.data.success || res.data.status)) {
        const updated = res.data.data;
        setAccounts((prev) => prev.map((a) => (a._id === account._id ? updated : a)));
        toast.success("Corporate account updated");
      } else {
        toast.error(res.data?.message || "Failed to update corporate account");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to update corporate account");
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(account._id);
        return next;
      });
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => {
      const name = String(a.companyName || "").toLowerCase();
      const email = String(a.admin?.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [accounts, search]);

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Corporate Accounts</h1>
        <div className="flex items-center gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company or admin email"
            className="border border-gray-300 rounded-md px-3 py-2 w-72"
          />
          <button
            onClick={fetchAccounts}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        {isLoading ? (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Company</th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Admin</th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Staff</th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Billing Model</th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Limit / Balance</th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Status</th>
                  <th className="text-right text-xs font-semibold text-gray-600 uppercase px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-gray-500 text-center" colSpan={7}>
                      No corporate accounts found
                    </td>
                  </tr>
                ) : (
                  filtered.map((account) => {
                    const isSaving = savingIds.has(account._id);
                    const staffCount = Number.isFinite(Number(account.staffCount)) ? Number(account.staffCount) : 0;
                    return (
                      <tr key={account._id} className="border-t">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{account.companyName || "(No company name)"}</div>
                          <div className="text-xs text-gray-500">Employees: {account.totalEmployees || 0}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">{account.admin?.name || "-"}</div>
                          <div className="text-xs text-gray-600">{account.admin?.email || "-"}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">{staffCount}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <select
                               className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                              value={account.billingModel || "credit_limit"}
                              onChange={(e) => updateLocalField(account._id, "billingModel", e.target.value)}
                              title="Credit Limit: Only uses allocated credit. Wallet: Uses both credit and wallet balance together."
                            >
                              <option value="credit_limit">Credit Limit</option>
                              <option value="wallet">Wallet</option>
                            </select>
                            <div className="text-xs text-gray-500">
                              {(account.billingModel || "credit_limit") === "credit_limit" ? (
                                "Uses: Credit only"
                              ) : (
                                "Uses: Credit + Wallet"
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {(account.billingModel || "credit_limit") === "credit_limit" ? (
                            <div className="flex flex-col gap-1">
                              <input
                                type="number"
                                min={0}
                                className="border border-gray-300 rounded-md px-2 py-1 w-32 text-sm"
                                value={account.creditLimit || 0}
                                onChange={(e) => updateLocalField(account._id, "creditLimit", e.target.value)}
                              />
                              <div className="text-xs text-gray-500">Used: {account.creditUsed || 0}</div>
                            </div>
                          ) : (
                            <input
                              type="number"
                              min={0}
                              className="border border-gray-300 rounded-md px-2 py-1 w-32 text-sm"
                              value={account.walletBalance || 0}
                              onChange={(e) => updateLocalField(account._id, "walletBalance", e.target.value)}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => {
                              if (isSaving) return;
                              const nextActive = !account.isActive;
                              updateLocalField(account._id, "isActive", nextActive);
                              saveAccount({ ...account, isActive: nextActive });
                            }}
                            className={`px-3 py-1 rounded text-sm font-medium ${
                              account.isActive ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"
                            }`}
                          >
                            {account.isActive ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => fetchStaff(account)}
                              className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-800 hover:bg-gray-50 text-xs font-semibold"
                            >
                              View Staff
                            </button>
                            <button
                              onClick={() => fetchCorporateRides(account)}
                              className="px-3 py-1.5 rounded-md border border-slate-300 bg-slate-50 text-slate-800 hover:bg-slate-100 text-xs font-semibold"
                            >
                              View Rides
                            </button>
                            <button
                              disabled={isSaving}
                              onClick={() => saveAccount(account)}
                              className={`px-3 py-1.5 rounded-md text-white text-xs font-semibold ${
                                isSaving ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
                              }`}
                            >
                              {isSaving ? "Saving..." : "Save"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Staff List Modal */}
      {staffModalAccount ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-4xl bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <div className="text-lg font-semibold text-gray-900">Staff List</div>
                <div className="text-xs text-gray-600">
                  {staffModalAccount.companyName || "(No company name)"}
                </div>
              </div>
              <button
                onClick={() => {
                  setStaffModalAccount(null);
                  setStaffList([]);
                }}
                className="px-3 py-2 rounded-md border border-gray-300 text-gray-800 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="p-5">
              {staffLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading staff...</span>
                </div>
              ) : staffList.length === 0 ? (
                <div className="text-gray-600">No staff found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Name</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Email</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Phone</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Designation</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffList.map((m) => (
                        <tr key={m._id || m.email} className="border-t">
                          <td className="px-4 py-3 text-sm text-gray-900">{m.fullName || "-"}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{m.email || "-"}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{m.mobileNumber || "-"}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{m.designation || "-"}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-3 py-1 rounded text-sm font-medium ${
                                m.isActive ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"
                              }`}
                            >
                              {m.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Rides List Modal */}
      {ridesModalAccount ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50">
              <div>
                <div className="text-lg font-black text-slate-800 tracking-tight">Corporate Rides & Bookings</div>
                <div className="text-xs text-slate-500 font-semibold mt-0.5">
                  {ridesModalAccount.companyName || "(No company name)"}
                </div>
              </div>
              <button
                onClick={() => {
                  setRidesModalAccount(null);
                  setRidesList([]);
                  setRideSearch("");
                }}
                className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 text-sm font-bold transition-all"
              >
                Close Rides
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {ridesLoading ? (
                <div className="flex items-center justify-center py-20 gap-3 flex-col">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                  <span className="text-sm font-semibold text-slate-500">Loading corporate rides...</span>
                </div>
              ) : (() => {
                const filteredRides = ridesList.filter((ride) => {
                  if (!rideSearch) return true;
                  return (ride.bookingId || "").toLowerCase().includes(rideSearch.toLowerCase());
                });
                
                return (
                  <div className="space-y-4">
                    {/* Search Bar for Booking ID */}
                    {ridesList.length > 0 && (
                      <div className="flex items-center gap-3 bg-white p-3 rounded-2xl border border-slate-100 shadow-sm">
                        <div className="relative flex-1 max-w-md">
                          <input
                            type="text"
                            placeholder="Search by Booking ID..."
                            value={rideSearch}
                            onChange={(e) => setRideSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs font-semibold"
                          />
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        {rideSearch && (
                          <button
                            onClick={() => setRideSearch("")}
                            className="text-xs font-bold text-blue-600 hover:text-blue-800"
                          >
                            Clear
                          </button>
                        )}
                        <span className="text-xs text-slate-400 font-bold ml-auto">
                          Found {filteredRides.length} {filteredRides.length === 1 ? 'ride' : 'rides'}
                        </span>
                      </div>
                    )}

                    {filteredRides.length === 0 ? (
                      <div className="text-center py-12 text-slate-500 font-semibold bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                        {ridesList.length === 0
                          ? "No corporate rides found for this account."
                          : `No rides match search query "${rideSearch}".`}
                      </div>
                    ) : (
                      <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
                        <table className="min-w-full divide-y divide-slate-100">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left text-xs font-bold text-slate-500 uppercase px-4 py-3">Booking #</th>
                              <th className="text-left text-xs font-bold text-slate-500 uppercase px-4 py-3">Date</th>
                              <th className="text-left text-xs font-bold text-slate-500 uppercase px-4 py-3">Employee/Staff</th>
                              <th className="text-left text-xs font-bold text-slate-500 uppercase px-4 py-3">Route</th>
                              <th className="text-left text-xs font-bold text-slate-500 uppercase px-4 py-3">Driver</th>
                              <th className="text-left text-xs font-bold text-slate-500 uppercase px-4 py-3">Fare</th>
                              <th className="text-left text-xs font-bold text-slate-500 uppercase px-4 py-3">Status</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-slate-100">
                            {filteredRides.map((ride) => (
                              <tr key={ride._id} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-4 py-3 text-sm font-bold text-blue-600 font-mono">{ride.bookingId || "—"}</td>
                                <td className="px-4 py-3 text-sm text-slate-600">
                                  {ride.createdAt ? new Date(ride.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <div className="font-bold text-slate-800">
                                    {ride.corporateMemberId?.fullName || "Guest Staff"}
                                  </div>
                                  <div className="text-xs text-slate-400 font-semibold">
                                    {ride.corporateMemberId?.designation || "Employee"} • {ride.corporateMemberId?.mobileNumber || "—"}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">
                                  <span className="font-bold text-slate-700">{ride.pickupLocation?.address?.split(',')[0]}</span>
                                  <span className="text-xs text-slate-400 mx-1">to</span>
                                  <span className="font-bold text-slate-700">{ride.dropLocation?.address?.split(',')[0]}</span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  {ride.driver ? (
                                    <>
                                      <div className="font-bold text-slate-800">{ride.driver.name}</div>
                                      <div className="text-xs text-slate-400 font-semibold">{ride.driver.phone}</div>
                                    </>
                                  ) : (
                                    <span className="text-slate-400 italic">Not Assigned</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm font-bold text-slate-800">
                                  ₹{(ride.totalAmount || 0).toLocaleString('en-IN')}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${
                                    ride.status === "completed" ? "bg-green-100 text-green-800" :
                                    ride.status === "cancelled" ? "bg-red-100 text-red-800" :
                                    "bg-blue-100 text-blue-800"
                                  }`}>
                                    {ride.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Corporates;
